import React, { useState, useEffect } from "react";
import { getAllUsers, getUserDetail, updateUser } from "../../services/platformApi";
import "./AdminOnboarding.css";
import "./EmployeeManagement.css";

/**
 * EmployeeManagement — admin sees all employees as profile-card grid,
 * drills into a split profile-photo detail view, and edits
 * Date of Joining / Relieving Date / Role / Status.
 * Redesign prefix: em2-*  (logic unchanged)
 * Place at: src/components/admin/EmployeeManagement.js
 */

const AV_GRADS = 5;
const avClass = (name = "") => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return `em2-av${h % AV_GRADS}`;
};
const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0] || "").join("").toUpperCase() || "?";

// Resolve a usable image URL from any shape the backend might send.
// Mirrors OnboardingDetail's docUrl pattern + extra fallbacks:
//   docs.photo | docs.photoUrl | docs.photo_url  (strings)
//   docs.photo.{previewUrl|url|signedUrl|href}   (object)
//   top-level photoUrl | photo | profilePhotoUrl | avatarUrl
const resolvePhoto = (obj = {}) => {
  if (!obj) return "";
  const docs = obj.documents || {};
  const dp = docs.photo;
  const fromDoc =
    typeof dp === "string"
      ? dp
      : (dp && (dp.previewUrl || dp.url || dp.signedUrl || dp.href)) || "";
  const url =
    obj.photoUrl ||
    obj.photo ||
    obj.profilePhotoUrl ||
    obj.avatarUrl ||
    docs.photoUrl ||
    docs.photo_url ||
    fromDoc ||
    "";
  return typeof url === "string" ? url : "";
};

function EmployeeManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("ALL"); // ALL | ACTIVE | DISABLED
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getAllUsers();
      setUsers(res.users || []);
    } catch (err) {
      setError(err.message || "Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = users.filter((u) => {
    const okStatus = filter === "ALL" ? true : u.status === filter;
    if (!okStatus) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      (u.fullName || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.employeeId || "").toLowerCase().includes(q) ||
      (u.department || "").toLowerCase().includes(q)
    );
  });

  const activeCount = users.filter((u) => u.status === "ACTIVE").length;
  const inactiveCount = users.filter((u) => u.status === "DISABLED").length;

  const fmtDate = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    if (isNaN(dt)) return "-";
    return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  if (selectedId) {
    return (
      <EmployeeDetail
        userId={selectedId}
        onBack={() => { setSelectedId(null); load(); }}
      />
    );
  }

  return (
    <div className="admin-onboarding">
      {/* Premium band header */}
      <div className="em2-band">
        <div className="em2-band__row">
          <div className="em2-brand">
            <div className="em2-brand__logo">
              <svg viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
            </div>
            <div className="em2-brand__txt">
              <h1>Employees</h1>
              <p>View &amp; manage all employee records</p>
            </div>
          </div>
          <div className="em2-toolbar">
            <div className="em2-search">
              <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4-4"/></svg>
              <input
                placeholder="Search name, ID, dept…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && (
                <button className="em2-search__clear" onClick={() => setQuery("")} aria-label="Clear">
                  <svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
              )}
            </div>
            <button className="em2-gbtn" onClick={load} disabled={loading} title="Refresh">
              <svg viewBox="0 0 24 24"><path d="M23 4v6h-6M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="em2-stats">
        <div className="em2-card em2-card--green">
          <div className="em2-card__top">
            <span className="em2-card__ic em2-ic-green">
              <svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg>
            </span>
            <span className="em2-card__num">{activeCount}</span>
          </div>
          <span className="em2-card__lbl">Active</span>
        </div>
        <div className="em2-card em2-card--amber">
          <div className="em2-card__top">
            <span className="em2-card__ic em2-ic-amber">
              <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
            </span>
            <span className="em2-card__num">{inactiveCount}</span>
          </div>
          <span className="em2-card__lbl">Inactive</span>
        </div>
        <div className="em2-card em2-card--navy">
          <div className="em2-card__top">
            <span className="em2-card__ic em2-ic-navy">
              <svg viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
            </span>
            <span className="em2-card__num">{users.length}</span>
          </div>
          <span className="em2-card__lbl">Total</span>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="em2-tabs">
        {["ALL", "ACTIVE", "DISABLED"].map((f) => (
          <button
            key={f}
            className={`em2-tab ${filter === f ? "em2-tab--on" : ""}`}
            onClick={() => setFilter(f)}
          >
            {f === "DISABLED" ? "Inactive" : f.charAt(0) + f.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {error && (
        <div className="em2-error">
          <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
          <span>{error}</span>
          <button onClick={load}>Retry</button>
        </div>
      )}
      {loading && (
        <div className="em2-loading"><div className="em2-spinner" /><span>Loading employees…</span></div>
      )}

      {!loading && !error && (
        filtered.length === 0 ? (
          <div className="em2-empty">
            <div className="em2-empty__ic">
              <svg viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
            </div>
            <h3>No employees found</h3>
            <p>Try a different search or filter.</p>
          </div>
        ) : (
          <div className="em2-grid">
            {filtered.map((u) => (
              <div key={u.id} className="em2-pcard" onClick={() => setSelectedId(u.id)}>
                <div className={`em2-pcap ${u.status === "ACTIVE" ? "" : "em2-pcap--off"}`}>
                  <span className={`em2-psb ${u.status === "ACTIVE" ? "em2-psb--on" : "em2-psb--off"}`}>
                    <i /> {u.status === "ACTIVE" ? "Active" : "Inactive"}
                  </span>
                </div>
                {resolvePhoto(u) ? (
                  <img
                    className="em2-pimg"
                    src={resolvePhoto(u)}
                    alt={u.fullName}
                    onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
                  />
                ) : null}
                <div
                  className={`em2-pimg em2-pimg--ph ${avClass(u.fullName)}`}
                  style={{ display: resolvePhoto(u) ? "none" : "flex" }}
                >
                  {initials(u.fullName)}
                </div>
                <div className="em2-pbody">
                  <b>{u.fullName}</b>
                  <div className="em2-pemail">{u.email}</div>
                  <div className="em2-pmeta">
                    {u.department && <span className="em2-chip">{u.department}</span>}
                    <span className="em2-chip">{u.role}</span>
                  </div>
                  <div className="em2-pdet">
                    <div><div className="em2-k">Emp ID</div><div className="em2-v">{u.employeeId || "-"}</div></div>
                    <div><div className="em2-k">Joining</div><div className="em2-v">{fmtDate(u.dateOfJoining)}</div></div>
                    <div><div className="em2-k">Designation</div><div className="em2-v">{u.designation || "-"}</div></div>
                    <div><div className="em2-k">Tenure</div><div className="em2-v">{u.tenure || "-"}</div></div>
                  </div>
                  <button className="em2-pmanage" onClick={(e) => { e.stopPropagation(); setSelectedId(u.id); }}>
                    <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
                    Manage
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

// ---------- Detail + Edit (split profile-photo layout) ----------
function EmployeeDetail({ userId, onBack }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [preview, setPreview] = useState(null); // { label, url }

  const [form, setForm] = useState({ dateOfJoining: "", relievingDate: "", role: "EMPLOYEE" });

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getUserDetail(userId);
      const data = res.data || res;
      setD(data);
      setForm({
        dateOfJoining: data.dateOfJoining || "",
        relievingDate: data.relievingDate || "",
        role: data.role || "EMPLOYEE",
      });
    } catch (err) {
      setError(err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const save = async (patch, successMsg) => {
    setSaving(true);
    setMsg("");
    try {
      await updateUser(userId, patch);
      setMsg(successMsg || "Saved");
      await load();
    } catch (err) {
      setMsg(err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="em2-loading"><div className="em2-spinner" /><span>Loading…</span></div>;
  if (error) return (
    <div className="em2-loading em2-loading--err">
      <span>{error}</span>
      <button className="em2-back" onClick={onBack}>← Back</button>
    </div>
  );

  const fmt = (x) => (x ? new Date(x).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-");
  const photo = resolvePhoto(d);
  const isImg = (url = "") => /\.(png|jpe?g|gif|webp|bmp)(\?|$)/i.test(url);

  return (
    <div className="onboarding-detail">
      <button className="em2-back" onClick={onBack}>
        <svg viewBox="0 0 24 24"><path d="M19 12H5M12 19l-7-7 7-7"/></svg> Back to Employees
      </button>

      <div className="em2-split">
        {/* LEFT profile photo card */}
        <div className="em2-pside">
          <div className={`em2-phero ${d.status === "ACTIVE" ? "" : "em2-phero--off"}`} />
          {photo ? (
            <img
              className="em2-pphoto"
              src={photo}
              alt={d.fullName}
              onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
            />
          ) : null}
          <div
            className={`em2-pphoto em2-pphoto--ph ${avClass(d.fullName)}`}
            style={{ display: photo ? "none" : "flex" }}
          >
            {initials(d.fullName)}
          </div>
          <div className="em2-pside__body">
            <h2>{d.fullName}</h2>
            <div className="em2-prole">{d.designation || d.role}</div>
            <div className="em2-pmail">{d.email}</div>
            <span className={`em2-psb2 ${d.status === "ACTIVE" ? "em2-psb--on" : "em2-psb--off"}`}>
              <i /> {d.status === "ACTIVE" ? "Active" : "Inactive"}
            </span>
            <div className="em2-pquick">
              <div><div className="em2-k">Emp ID</div><div className="em2-v">{d.employeeId || "-"}</div></div>
              <div><div className="em2-k">Dept</div><div className="em2-v">{d.department || "-"}</div></div>
              <div><div className="em2-k">Tenure</div><div className="em2-v em2-v--hl">{d.tenure || "-"}</div></div>
              <div><div className="em2-k">Role</div><div className="em2-v">{d.role}</div></div>
            </div>
          </div>
        </div>

        {/* RIGHT content */}
        <div className="em2-right">
          {msg && (
            <div className="em2-msg">
              <svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg> {msg}
            </div>
          )}

          {/* Personal info */}
          <div className="em2-sec">
            <div className="em2-sec__title">
              <svg viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
              Personal Information
            </div>
            <div className="em2-dgrid">
              <Info label="Designation" value={d.designation} />
              <Info label="Phone" value={d.phone ? `+91 ${d.phone}` : "-"} />
              <Info label="Personal Email" value={d.personalEmail} />
              <Info label="Blood Group" value={d.bloodGroup} />
              <Info label="Current City" value={d.currentCity} />
              <Info label="Date of Joining" value={fmt(d.dateOfJoining)} />
            </div>
          </div>

          {/* Manage */}
          <div className="em2-sec">
            <div className="em2-sec__title">
              <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
              Manage Employee
            </div>
            <div className="em2-mgrid">
              {/* Employment dates */}
              <div className="em2-mbox">
                <h5>
                  <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                  Employment Dates
                </h5>
                <div className="em2-mf">
                  <label>Date of Joining</label>
                  <input type="date" value={form.dateOfJoining || ""}
                         onChange={(e) => setForm((f) => ({ ...f, dateOfJoining: e.target.value }))} />
                </div>
                <div className="em2-mf">
                  <label>Relieving Date</label>
                  <input type="date" value={form.relievingDate || ""}
                         onChange={(e) => setForm((f) => ({ ...f, relievingDate: e.target.value }))} />
                  <span className="em2-hint">Setting this deactivates the employee.</span>
                </div>
                <button className="em2-bapp" disabled={saving}
                        onClick={() => save({ dateOfJoining: form.dateOfJoining, relievingDate: form.relievingDate }, "Dates updated")}>
                  Save Dates
                </button>
              </div>

              {/* Role & status */}
              <div className="em2-mbox">
                <h5>
                  <svg viewBox="0 0 24 24"><path d="M12 15a3 3 0 100-6 3 3 0 000 6z"/><path d="M2 12h4M18 12h4M12 2v4M12 18v4"/></svg>
                  Role &amp; Status
                </h5>
                <div className="em2-mf">
                  <label>Role</label>
                  <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
                    <option value="EMPLOYEE">Employee</option>
                    <option value="MANAGER">Manager</option>
                    <option value="ADMIN">Admin</option>
                  </select>
                </div>
                <button className="em2-bapp" disabled={saving || form.role === d.role}
                        onClick={() => save({ role: form.role }, `Role changed to ${form.role}`)}>
                  Update Role
                </button>
                <div className="em2-stgl">
                  <span>Account Status</span>
                  <span className={`em2-psb2 ${d.status === "ACTIVE" ? "em2-psb--on" : "em2-psb--off"}`}>
                    <i /> {d.status === "ACTIVE" ? "Active" : "Inactive"}
                  </span>
                </div>
                {d.status === "ACTIVE" ? (
                  <button className="em2-brej" disabled={saving}
                          onClick={() => save({ status: "DISABLED" }, "Employee deactivated")}>
                    Deactivate Employee
                  </button>
                ) : (
                  <button className="em2-bapp" disabled={saving}
                          onClick={() => save({ status: "ACTIVE" }, "Employee activated")}>
                    Activate Employee
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="em2-sec">
            <div className="em2-sec__title">
              <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/></svg>
              Documents
            </div>
            <div className="em2-docs">
              {d.documents?.aadhar && (
                <button className="em2-docbtn" onClick={() => setPreview({ label: "Aadhar Card", url: d.documents.aadhar })}>
                  <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/></svg>
                  Aadhar Card
                </button>
              )}
              {d.documents?.pan && (
                <button className="em2-docbtn" onClick={() => setPreview({ label: "PAN Card", url: d.documents.pan })}>
                  <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/></svg>
                  PAN Card
                </button>
              )}
              {d.documents?.photo && (
                <button className="em2-docbtn" onClick={() => setPreview({ label: "Passport Photo", url: d.documents.photo })}>
                  <svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                  Passport Photo
                </button>
              )}
              {!d.documents?.aadhar && !d.documents?.pan && !d.documents?.photo && (
                <span className="em2-nodoc">No documents uploaded.</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Document preview modal */}
      {preview && (
        <div className="em2-modal" onClick={() => setPreview(null)}>
          <div className="em2-modal__box" onClick={(e) => e.stopPropagation()}>
            <div className="em2-modal__head">
              <h4>{preview.label}</h4>
              <button className="em2-modal__close" onClick={() => setPreview(null)} aria-label="Close">
                <svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="em2-modal__body">
              {isImg(preview.url) ? (
                <img className="em2-modal__img" src={preview.url} alt={preview.label} />
              ) : (
                <iframe className="em2-modal__pdf" title={preview.label} src={preview.url} />
              )}
            </div>
            <div className="em2-modal__foot">
              <a className="em2-docbtn" href={preview.url} target="_blank" rel="noopener noreferrer">
                Open in new tab
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value, highlight }) {
  return (
    <div className="em2-ditem">
      <div className="em2-k">{label}</div>
      <div className={`em2-dv ${highlight ? "em2-dv--hl" : ""}`}>{value || "-"}</div>
    </div>
  );
}

export default EmployeeManagement;
